import { Story } from '../../context/AppContext';
import { RenderListItem } from '../../api/rendersApi';

export interface DiscoveryRail {
  id: string;
  title: string;
  description: string;
  stories: Story[];
}

const SLEEPY_TAGS = new Set([
  'bedtime',
  'dreams',
  'sleep',
  'sleepy',
  'wishes',
  'moon',
]);

const ADVENTURE_TAGS = new Set([
  'adventure',
  'dragons',
  'forest',
  'magic',
  'pirates',
  'ships',
  'space',
  'trains',
]);

function hasAnyTag(story: Story, tags: Set<string>): boolean {
  return story.tags.some((tag) => tags.has(tag.toLowerCase()));
}

export function chooseTonightStory(
  stories: Story[],
  renderByStory: Record<string, RenderListItem>
): Story | null {
  const ranked = [...stories].sort((left, right) => {
    const leftRender = renderByStory[left.story_id];
    const rightRender = renderByStory[right.story_id];
    const rank = (render?: RenderListItem) => {
      if (render?.status === 'parent_approved') return 0;
      if (render?.status === 'machine_passed') return 1;
      if (render?.status === 'needs_review') return 2;
      if (render) return 3;
      return 4;
    };
    const rankDifference = rank(leftRender) - rank(rightRender);
    if (rankDifference !== 0) return rankDifference;
    const leftCreated = leftRender?.created_at ?? '';
    const rightCreated = rightRender?.created_at ?? '';
    return rightCreated.localeCompare(leftCreated);
  });
  return ranked[0] ?? null;
}

export function buildDiscoveryRails(
  stories: Story[],
  renderByStory: Record<string, RenderListItem>,
  featuredStoryId?: string
): DiscoveryRail[] {
  const used = new Set<string>(featuredStoryId ? [featuredStoryId] : []);

  // Eight per rail so a shelf overflows its viewport and the clipped preview
  // and Next arrow have something to reveal.
  const claim = (predicate: (story: Story) => boolean, max = 8): Story[] => {
    const matches = stories
      .filter((story) => !used.has(story.story_id) && predicate(story))
      .slice(0, max);
    matches.forEach((story) => used.add(story.story_id));
    return matches;
  };

  const definitions: Array<Omit<DiscoveryRail, 'stories'> & { stories: Story[] }> = [
    {
      id: 'ready-library',
      title: 'Ready for bedtime',
      description: 'Already approved for this listener and Family voice.',
      stories: claim(
        (story) => renderByStory[story.story_id]?.status === 'parent_approved'
      ),
    },
    {
      id: 'quick-bedtime',
      title: 'Quick bedtime',
      description: 'Gentle choices for a shorter wind-down.',
      stories: claim(
        (story) => (story.estimated_duration_minutes ?? Number.POSITIVE_INFINITY) <= 5
      ),
    },
    {
      id: 'very-sleepy',
      title: 'Very sleepy',
      description: 'Dreamy stories with a quiet bedtime shape.',
      stories: claim((story) => hasAnyTag(story, SLEEPY_TAGS)),
    },
    {
      id: 'gentle-adventure',
      title: 'Gentle adventure',
      description: 'A little wonder, with a calm way home.',
      stories: claim(
        (story) => story.theme === 'adventure' || hasAnyTag(story, ADVENTURE_TAGS)
      ),
    },
    {
      id: 'longer-wind-down',
      title: 'Longer wind-down',
      description: 'Settle in when bedtime has room for more story.',
      stories: claim((story) => (story.estimated_duration_minutes ?? 0) >= 8),
    },
    {
      id: 'more-stories',
      title: 'More gentle stories',
      description: 'The rest of tonight’s reviewed Luma collection.',
      stories: claim(() => true, Number.POSITIVE_INFINITY),
    },
  ];

  return definitions.filter((rail) => rail.stories.length > 0);
}
